import React, { useEffect, useState } from 'react';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { DollarSignIcon, BarChartIcon, TrendingUpIcon, ArrowRightIcon, CheckIcon, XIcon, UserIcon } from 'lucide-react';
export const PricingStrategiesPage = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);
  return <div className="w-full bg-gray-50 dark:bg-[#0A1C1A] min-h-screen" data-id="element-5764">
      <Navbar scrolled={scrolled} data-id="element-5765" />
      <main className="pt-24 pb-20" data-id="element-5766">
        <div className="container mx-auto px-4 md:px-6" data-id="element-5767">
          <div className="max-w-4xl mx-auto" data-id="element-5768">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] rounded-xl p-8 md:p-12 mb-12 text-white relative overflow-hidden" data-id="element-5769">
              <div className="absolute right-0 bottom-0 opacity-10" data-id="element-5770">
                <DollarSignIcon className="h-64 w-64" data-id="element-5771" />
              </div>
              <div className="relative z-10" data-id="element-5772">
                <h1 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-5773">
                  Smart Pricing Strategies for Digital Creators
                </h1>
                <p className="text-xl opacity-90 max-w-3xl" data-id="element-5774">
                  Learn how to set the right price for your templates, UI kits,
                  and digital assets using psychological pricing, A/B testing,
                  and competitive research.
                </p>
              </div>
            </div>
            {/* Main content */}
            <div className="bg-white dark:bg-[#162825] rounded-xl p-8 md:p-10 shadow-sm mb-10" data-id="element-5775">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-5776">
                Why Pricing Matters
              </h2>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6" data-id="element-5777">
                Setting the right price for your digital products is one of the
                most critical decisions you'll make as a creator. Price too
                high, and you might scare away potential customers. Price too
                low, and you devalue your work while leaving money on the table.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6" data-id="element-5778">
                The good news is that digital products have unique economics
                that work in your favor - once created, they can be sold
                infinitely with minimal additional costs. This guide will help
                you navigate pricing strategies that maximize both sales volume
                and revenue.
              </p>
              <div className="border-l-4 border-[#2DB489] dark:border-[#4ED1A0] pl-6 my-8 py-2" data-id="element-5779">
                <p className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9] italic" data-id="element-5780">
                  "The right price is not about charging as much as possible -
                  it's about finding the sweet spot where customers feel they're
                  getting excellent value while you're being fairly compensated
                  for your expertise."
                </p>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mt-12 mb-6" data-id="element-5781">
                Psychological Pricing Techniques
              </h2>
              <div className="mb-8" data-id="element-5782">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4 flex items-center" data-id="element-5783">
                  <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-8 h-8 rounded-full flex items-center justify-center mr-3" data-id="element-5784">
                    1
                  </span>
                  Charm Pricing
                </h3>
                <p className="text-gray-600 dark:text-[#B0C1BD] mb-4 ml-11" data-id="element-5785">
                  Ending prices with .99 or .97 (e.g., $19.99 instead of $20)
                  has been proven to increase sales. Studies show that consumers
                  tend to focus on the first digit and perceive these prices as
                  significantly lower.
                </p>
                <div className="ml-11 grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-5786">
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-4 rounded-lg" data-id="element-5787">
                    <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-5788">
                      Example:
                    </p>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5789">
                      A UI kit priced at $49.99 often psychologically feels
                      closer to $40 than $50 to customers.
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-4 rounded-lg" data-id="element-5790">
                    <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-5791">
                      When to use:
                    </p>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5792">
                      For products targeting price-sensitive customers or in
                      competitive niches.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mb-8" data-id="element-5793">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4 flex items-center" data-id="element-5794">
                  <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-8 h-8 rounded-full flex items-center justify-center mr-3" data-id="element-5795">
                    2
                  </span>
                  Prestige Pricing
                </h3>
                <p className="text-gray-600 dark:text-[#B0C1BD] mb-4 ml-11" data-id="element-5796">
                  Using round numbers (e.g., $100 instead of $99.99) can signal
                  quality and premium positioning. This works well for high-end
                  digital products where you want to emphasize craftsmanship and
                  value.
                </p>
                <div className="ml-11 grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-5797">
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-4 rounded-lg" data-id="element-5798">
                    <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-5799">
                      Example:
                    </p>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5800">
                      A comprehensive design system priced at $200 rather than
                      $199 can be perceived as more professional and
                      high-quality.
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-4 rounded-lg" data-id="element-5801">
                    <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-5802">
                      When to use:
                    </p>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5803">
                      For premium products where you want to signal exceptional
                      quality and comprehensive value.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mb-8" data-id="element-5804">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4 flex items-center" data-id="element-5805">
                  <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-8 h-8 rounded-full flex items-center justify-center mr-3" data-id="element-5806">
                    3
                  </span>
                  Anchoring
                </h3>
                <p className="text-gray-600 dark:text-[#B0C1BD] mb-4 ml-11" data-id="element-5807">
                  Showing a higher "original" price next to your actual price
                  creates a reference point that makes your current price seem
                  like a better deal.
                </p>
                <div className="ml-11 grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-5808">
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-4 rounded-lg" data-id="element-5809">
                    <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-5810">
                      Example:
                    </p>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5811">
                      Displaying "$149 $99" for your website template makes $99
                      seem like an excellent value.
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-4 rounded-lg" data-id="element-5812">
                    <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-5813">
                      When to use:
                    </p>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5814">
                      During promotions, launches, or when you want to emphasize
                      the value proposition.
                    </p>
                  </div>
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mt-12 mb-6" data-id="element-5815">
                Practical Pricing Framework
              </h2>
              <div className="bg-gray-50 dark:bg-[#1A3330] p-6 rounded-xl mb-8" data-id="element-5816">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-5817">
                  Step-by-Step Pricing Process
                </h3>
                <ol className="space-y-4" data-id="element-5818">
                  <li className="flex" data-id="element-5819">
                    <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-5820">
                      1
                    </span>
                    <div data-id="element-5821">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-5822">
                        Research competitors
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5823">
                        Identify 5-10 similar products and note their pricing,
                        features, and positioning.
                      </p>
                    </div>
                  </li>
                  <li className="flex" data-id="element-5824">
                    <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-5825">
                      2
                    </span>
                    <div data-id="element-5826">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-5827">
                        Calculate your costs
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5828">
                        Include time spent, software costs, and platform fees.
                      </p>
                    </div>
                  </li>
                  <li className="flex" data-id="element-5829">
                    <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-5830">
                      3
                    </span>
                    <div data-id="element-5831">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-5832">
                        Assess your unique value
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5833">
                        What makes your product better or different? This can
                        justify higher pricing.
                      </p>
                    </div>
                  </li>
                  <li className="flex" data-id="element-5834">
                    <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-5835">
                      4
                    </span>
                    <div data-id="element-5836">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-5837">
                        Consider your target audience
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5838">
                        Are they professionals who can expense purchases?
                        Hobbyists? Students?
                      </p>
                    </div>
                  </li>
                  <li className="flex" data-id="element-5839">
                    <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-5840">
                      5
                    </span>
                    <div data-id="element-5841">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-5842">
                        Test multiple price points
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5843">
                        If possible, A/B test different prices or adjust pricing
                        over time to find the optimal point.
                      </p>
                    </div>
                  </li>
                </ol>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mt-12 mb-6" data-id="element-5844">
                Pricing Models for Digital Products
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-5845">
                <div className="bg-white dark:bg-[#162825] border border-gray-200 dark:border-[#1A3330] rounded-xl p-6 shadow-sm" data-id="element-5846">
                  <div className="flex items-center mb-4" data-id="element-5847">
                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-3" data-id="element-5848">
                      <BarChartIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" data-id="element-5849" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-5850">
                      Tiered Pricing
                    </h3>
                  </div>
                  <p className="text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-5851">
                    Offer multiple versions of your product at different price
                    points (e.g., Basic, Pro, Enterprise).
                  </p>
                  <div className="space-y-2" data-id="element-5852">
                    <div className="flex items-start" data-id="element-5853">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-5854" />
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5855">
                        Captures customers at different budget levels
                      </p>
                    </div>
                    <div className="flex items-start" data-id="element-5856">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-5857" />
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5858">
                        Creates natural upsell opportunities
                      </p>
                    </div>
                    <div className="flex items-start" data-id="element-5859">
                      <XIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-5860" />
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5861">
                        Can be complex to manage multiple versions
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-[#162825] border border-gray-200 dark:border-[#1A3330] rounded-xl p-6 shadow-sm" data-id="element-5862">
                  <div className="flex items-center mb-4" data-id="element-5863">
                    <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mr-3" data-id="element-5864">
                      <TrendingUpIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" data-id="element-5865" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-5866">
                      Freemium
                    </h3>
                  </div>
                  <p className="text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-5867">
                    Offer a free basic version with paid upgrades for premium
                    features.
                  </p>
                  <div className="space-y-2" data-id="element-5868">
                    <div className="flex items-start" data-id="element-5869">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-5870" />
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5871">
                        Lower barrier to entry for new customers
                      </p>
                    </div>
                    <div className="flex items-start" data-id="element-5872">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-5873" />
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5874">
                        Builds trust before asking for payment
                      </p>
                    </div>
                    <div className="flex items-start" data-id="element-5875">
                      <XIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-5876" />
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5877">
                        Free users may never convert to paid
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200 dark:border-[#1A3330] pt-10 mt-10" data-id="element-5878">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-5879">
                  Case Study: Successful Pricing Optimization
                </h2>
                <div className="bg-gray-50 dark:bg-[#1A3330] rounded-xl p-6 mb-8" data-id="element-5880">
                  <div className="flex items-center mb-4" data-id="element-5881">
                    <div className="w-12 h-12 rounded-full overflow-hidden mr-4" data-id="element-5882">
                      <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="Alex Morgan" className="w-full h-full object-cover" data-id="element-5883" />
                    </div>
                    <div data-id="element-5884">
                      <h3 className="font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-5885">
                        Alex Morgan
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-5886">
                        UI Kit Creator
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-5887">
                    "I initially priced my UI kit at $39, which was lower than
                    competitors because I was new to the market. After 3 months,
                    I raised the price to $59 and added more components to
                    justify the increase."
                  </p>
                  <p className="text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-5888">
                    "The surprising result: not only did sales volume stay
                    roughly the same, but my revenue increased by 51%. I later
                    introduced a basic version at $39 and an extended version at
                    $89, which further increased total revenue by offering
                    options for different customer segments."
                  </p>
                  <div className="mt-6 grid grid-cols-3 gap-4 text-center" data-id="element-5889">
                    <div className="bg-white dark:bg-[#162825] p-4 rounded-lg" data-id="element-5890">
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-5891">
                        Initial Price
                      </p>
                      <p className="text-xl font-bold text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-5892">
                        $39
                      </p>
                    </div>
                    <div className="bg-white dark:bg-[#162825] p-4 rounded-lg" data-id="element-5893">
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-5894">
                        Optimal Price
                      </p>
                      <p className="text-xl font-bold text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-5895">
                        $59
                      </p>
                    </div>
                    <div className="bg-white dark:bg-[#162825] p-4 rounded-lg" data-id="element-5896">
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-5897">
                        Revenue Increase
                      </p>
                      <p className="text-xl font-bold text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-5898">
                        +51%
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-[#0D3C45]/5 dark:bg-[#0D3C45]/20 rounded-xl p-6 mt-12" data-id="element-5899">
                <h2 className="text-xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-5900">
                  Key Takeaways
                </h2>
                <ul className="space-y-3" data-id="element-5901">
                  <li className="flex items-start" data-id="element-5902">
                    <div className="bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full p-1 mr-3 mt-1 flex-shrink-0" data-id="element-5903">
                      <CheckIcon className="h-3 w-3 text-white" data-id="element-5904" />
                    </div>
                    <p className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-5905">
                      Don't undervalue your work - many creators price too low
                      initially
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-5906">
                    <div className="bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full p-1 mr-3 mt-1 flex-shrink-0" data-id="element-5907">
                      <CheckIcon className="h-3 w-3 text-white" data-id="element-5908" />
                    </div>
                    <p className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-5909">
                      Test different price points - the market will tell you
                      what works
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-5910">
                    <div className="bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full p-1 mr-3 mt-1 flex-shrink-0" data-id="element-5911">
                      <CheckIcon className="h-3 w-3 text-white" data-id="element-5912" />
                    </div>
                    <p className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-5913">
                      Consider offering multiple tiers to capture different
                      customer segments
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-5914">
                    <div className="bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full p-1 mr-3 mt-1 flex-shrink-0" data-id="element-5915">
                      <CheckIcon className="h-3 w-3 text-white" data-id="element-5916" />
                    </div>
                    <p className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-5917">
                      Focus on communicating value, not just features or
                      specifications
                    </p>
                  </li>
                </ul>
              </div>
            </div>
            {/* Related resources */}
            <div className="bg-white dark:bg-[#162825] rounded-xl p-8 shadow-sm mb-10" data-id="element-5918">
              <h2 className="text-xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-5919">
                Related Resources
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5920">
                <a href="/learn/product-bundles" className="group block bg-gray-50 dark:bg-[#1A3330] rounded-lg p-5 transition-all hover:shadow-md" data-id="element-5921">
                  <h3 className="font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2 group-hover:text-[#2DB489] dark:group-hover:text-[#4ED1A0] transition-colors" data-id="element-5922">
                    Product Bundles That Sell
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-3" data-id="element-5923">
                    Learn how to create irresistible product bundles that
                    increase your average order value.
                  </p>
                  <div className="flex items-center text-[#2DB489] dark:text-[#4ED1A0] text-sm font-medium" data-id="element-5924">
                    <span data-id="element-5925">Read more</span>
                    <ArrowRightIcon className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" data-id="element-5926" />
                  </div>
                </a>
                <a href="/learn/limited-offers" className="group block bg-gray-50 dark:bg-[#1A3330] rounded-lg p-5 transition-all hover:shadow-md" data-id="element-5927">
                  <h3 className="font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2 group-hover:text-[#2DB489] dark:group-hover:text-[#4ED1A0] transition-colors" data-id="element-5928">
                    Creating Limited-Time Offers
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-3" data-id="element-5929">
                    Strategies for creating urgency and scarcity to boost sales
                    of your digital products.
                  </p>
                  <div className="flex items-center text-[#2DB489] dark:text-[#4ED1A0] text-sm font-medium" data-id="element-5930">
                    <span data-id="element-5931">Read more</span>
                    <ArrowRightIcon className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" data-id="element-5932" />
                  </div>
                </a>
              </div>
            </div>
            {/* CTA */}
            <div className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] rounded-xl p-8 text-white text-center" data-id="element-5933">
              <h2 className="text-2xl font-bold mb-4" data-id="element-5934">
                Ready to optimize your pricing?
              </h2>
              <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto" data-id="element-5935">
                Apply these strategies to your digital products and start
                maximizing your revenue today.
              </p>
              <a href="/sell-product" className="inline-block bg-white text-[#0D3C45] px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-all" data-id="element-5936">
                Start Selling
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-5937" />
    </div>;
};